import type {
  SearchParams as DirectorySearchBarParams,
  SmartSuggestionEntry,
} from '@centivo/provider-directory-components'

export type ResultsUrlParams = Record<string, string | string[] | null>

const CONDITION_SERVICE_LINES_DELIMITER = '|'

const getParam = (params: ResultsUrlParams, key: string): string => {
  const value = params[key]
  if (Array.isArray(value)) return value[0] ?? ''
  return value ?? ''
}

/**
 * Rebuilds the library `SearchParams` from `/results` URL query params.
 *
 * Returns `undefined` when no `placeId` is present, which is what drives the
 * Results landing state (a search cannot run without a resolved place).
 *
 * The param names are compatible with Home's existing submit
 * (`placeId`, `addressLabel`, `name`, `serviceLines`) and add `condition` /
 * `conditionServiceLines` for the library's condition suggestions, and
 * `smartQuery` for a smart suggestion entry.
 */
export function decodeResultsSearch(
  params: ResultsUrlParams
): DirectorySearchBarParams | undefined {
  const placeId = getParam(params, 'placeId')
  if (!placeId) return undefined

  const query = getParam(params, 'name')
  const zipCode = getParam(params, 'addressLabel')
  const serviceLineLabel = getParam(params, 'serviceLines')
  const conditionLabel = getParam(params, 'condition')
  const conditionServiceLines = getParam(params, 'conditionServiceLines')
  const smartQuery = getParam(params, 'smartQuery')

  const selectedServiceLine = serviceLineLabel
    ? { id: serviceLineLabel, label: serviceLineLabel }
    : undefined

  const selectedCondition = conditionLabel
    ? {
        id: conditionLabel,
        label: conditionLabel,
        aliases: [],
        serviceLines: conditionServiceLines
          ? conditionServiceLines.split(CONDITION_SERVICE_LINES_DELIMITER)
          : [],
        specialtyCodes: [],
      }
    : undefined

  const selectedSmartSuggestion: SmartSuggestionEntry | undefined = smartQuery
    ? { kind: 'smartSuggestion', query: smartQuery }
    : undefined

  return {
    query: selectedSmartSuggestion ? smartQuery : query,
    zipCode,
    placeId,
    selectedServiceLine,
    selectedCondition,
    selectedSmartSuggestion,
  }
}

/**
 * Serializes the library `SearchParams` into a `/results` query string.
 *
 * Mirrors how `buildDirectoryQueryParams` reads the search: a selected
 * service line is carried as its `label`, a selected condition as its label
 * plus its `serviceLines`, a smart suggestion as `smartQuery`, and free text
 * as `name` only when no taxonomy is chosen.
 */
export function encodeResultsSearch(params: DirectorySearchBarParams): string {
  const search = new URLSearchParams()

  if (params.placeId) search.set('placeId', params.placeId)
  if (params.zipCode) search.set('addressLabel', params.zipCode)

  const smartEntry =
    params.selectedSmartSuggestion && 'query' in params.selectedSmartSuggestion
      ? params.selectedSmartSuggestion
      : undefined

  if (smartEntry) {
    search.set('smartQuery', smartEntry.query)
  } else if (params.selectedServiceLine) {
    search.set('serviceLines', params.selectedServiceLine.label)
  } else if (params.selectedCondition) {
    search.set('condition', params.selectedCondition.label)
    if (params.selectedCondition.serviceLines.length) {
      search.set(
        'conditionServiceLines',
        params.selectedCondition.serviceLines.join(
          CONDITION_SERVICE_LINES_DELIMITER
        )
      )
    }
  } else if (params.query.trim()) {
    search.set('name', params.query.trim())
  }

  return search.toString()
}
